import PropTypes from 'prop-types';
import React, { lazy } from 'react';
import { withErrorBoundary, useErrorBoundary } from 'react-use-error-boundary';

import { blockNameTransform } from '~/utilities/helpers';

import Loader from '~/components/Loader';
import SuspenseHelper from '~/components/SuspenseHelper';

const availableBlocks = {
    ParagraphBlock: lazy(() => import('~/blocks/core/ParagraphBlock')),
    ImageBlock: lazy(() => import('~/blocks/core/ImageBlock')),
    HeadingBlock: lazy(() => import('~/blocks/core/HeadingBlock')),
    ListBlock: lazy(() => import('~/blocks/core/ListBlock')),
    ColumnsBlock: lazy(() => import('~/blocks/core/ColumnsBlock')),
    SpacerBlock: lazy(() => import('~/blocks/core/SpacerBlock')),
    HtmlBlock: lazy(() => import('~/blocks/core/HtmlBlock')),
    FreeformBlock: lazy(() => import('~/blocks/core/FreeformBlock')),
    BlockBlock: lazy(() => import('~/blocks/core/BlockBlock')),
    CtaBarBlock: lazy(() => import('~/blocks/acf/CtaBarBlock')),
    FormBlock: lazy(() => import('~/blocks/acf/FormBlock')),
    HeroBannerBlock: lazy(() => import('~/blocks/acf/HeroBannerBlock')),
    AnythingCardsBlock: lazy(() => import('~/blocks/acf/AnythingCardsBlock')),
    WashInstructionsBlock: lazy(
        () => import('~/blocks/acf/WashInstructionsBlock')
    ),
    FullWidthFeatureBlock: lazy(
        () => import('~/blocks/acf/FullWidthFeatureBlock')
    ),
    BrandFeatureBlock: lazy(() => import('~/blocks/acf/BrandFeatureBlock')),
    HalfWidthFeatureCtasBlock: lazy(
        () => import('~/blocks/acf/HalfWidthFeatureCtasBlock')
    ),
    FeaturedStoryBlock: lazy(() => import('~/blocks/acf/FeaturedStoryBlock')),
    NewsCardsBlock: lazy(() => import('~/blocks/acf/NewsCardsBlock')),
    FeaturesCarouselBlock: lazy(
        () => import('~/blocks/acf/FeaturesCarouselBlock')
    ),
    AccordionsBlock: lazy(() => import('~/blocks/acf/AccordionsBlock')),
    QuoteCarouselBlock: lazy(() => import('~/blocks/acf/QuoteCarouselBlock')),
    LocationsFinderBlock: lazy(
        () => import('~/blocks/acf/LocationsFinderBlock')
    ),
    CtaCardsBlock: lazy(() => import('~/blocks/acf/CtaCardsBlock')),
    BreadcrumbsBlock: lazy(() => import('~/blocks/acf/BreadcrumbsBlock')),
    AccountHeroBlock: lazy(
        () => import('~/account/blocks/acf/AccountHeroBlock')
    ),
    LegacyInvoiceBlock: lazy(
        () => import('~/account/blocks/acf/LegacyInvoiceBlock')
    ),
    LoginBlock: lazy(() => import('~/account/blocks/acf/LoginBlock')),
    ForgotPasswordBlock: lazy(
        () => import('~/account/blocks/acf/ForgotPasswordBlock')
    ),
    ConfirmForgotPasswordBlock: lazy(
        () => import('~/account/blocks/acf/ConfirmForgotPasswordBlock')
    ),
    ConfirmCodeBlock: lazy(
        () => import('~/account/blocks/acf/ConfirmCodeBlock')
    ),
    ResendConfirmCodeBlock: lazy(
        () => import('~/account/blocks/acf/ResendConfirmCodeBlock')
    ),
    RegisterAccountBlock: lazy(
        () => import('~/account/blocks/acf/RegisterAccountBlock')
    ),
    SignUpBlock: lazy(() => import('~/account/blocks/acf/SignUpBlock')),
    ForceChangePasswordBlock: lazy(
        () => import('~/account/blocks/acf/ForceChangePasswordBlock')
    ),
    AccountSettingsBlock: lazy(
        () => import('~/account/blocks/acf/AccountSettingsBlock')
    ),
    GaragesBlock: lazy(() => import('~/account/blocks/acf/GaragesBlock')),
    GiftCardsBlock: lazy(() => import('~/account/blocks/acf/GiftCardsBlock')),
    WashesBlock: lazy(() => import('~/account/blocks/acf/WashesBlock')),
    RewardsHeroBlock: lazy(
        () => import('~/account/blocks/acf/RewardsHeroBlock')
    )
};

const Block = withErrorBoundary(({ attrs, name, index, ...props }) => {
    const componentParts = blockNameTransform(name).split('/');
    const componentType = componentParts[0];
    const componentName = componentParts[1];
    let TheBlock = availableBlocks[componentName];
    const [error, resetError] = useErrorBoundary((error, errorInfo) =>
        console.error(error, errorInfo)
    );
    if (error) {
        return (
            <div>
                <h1>Something went wrong</h1>
                <button onClick={resetError}>Try again</button>
            </div>
        );
    }
    if (TheBlock === undefined) {
        TheBlock = availableBlocks[`${componentType}${componentName}`];
    }
    if (!TheBlock) {
        return <div>{name}</div>;
    }

    return TheBlock ? (
        <SuspenseHelper fallback={index === 0 ? <Loader /> : <></>}>
            <TheBlock {...{ attrs }} {...props} blockName={name} />
        </SuspenseHelper>
    ) : null;
});

Block.propTypes = {
    attrs: PropTypes.shape({}),
    name: PropTypes.string.isRequired,
    order: PropTypes.number.isRequired
};

const BlockList = ({ blocks }) =>
    blocks
        .filter((block) => block)
        .filter(({ name }) => name)
        .map((block, index) => (
            <Block {...block} {...{ index }} key={block.order} />
        ));

BlockList.propTypes = {
    blocks: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string
        })
    ).isRequired
};

export default BlockList;
